import { lockBodyScroll } from "../utils/scrollLock";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { supabase } from "../supabase";
import { createNotification, notifyMentions } from "../utils/notifications";
import { getBlockedUserIds } from "../utils/blocks";
import ReportModal from "./ReportModal";
import HashtagInput from "./HashtagInput";
import MentionText from "./MentionText";
import LazyImage from "./LazyImage";
import { IoClose, IoTrashOutline, IoSend, IoFlagOutline, IoChatbubbleOutline } from "react-icons/io5";
import { useLanguage } from "../context/LanguageContext";
import { friendlyError } from "../utils/errors";
import UndoToast from "./UndoToast";
import { useFocusTrap } from "../utils/focusTrap";
import VerifiedBadge from "./VerifiedBadge";

const PAGE_SIZE = 10;

export default function Comments({ designId, onClose, designOwnerId }) {
  const { t } = useLanguage();
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [user, setUser] = useState(null);
  const [myProfile, setMyProfile] = useState(null); // { username, avatar_url }
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const postingRef = useRef(false);
  const [error, setError] = useState(null);
  const [reportCommentId, setReportCommentId] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null); // { id, username } | null
  const [expandedReplies, setExpandedReplies] = useState({}); // { [parentId]: true }
  // Where each thread's replies have been fetched up to (the created_at of the last one
  // that came from the server), so "View more replies" carries on from there.
  const replyCursor = useRef({});
  // loadingRepliesFor (state) lags one render behind a click — a fast
  // double-tap, or collapse-then-immediately-re-expand while the original
  // fetch is still in flight, can fire loadRepliesPage a second time for the
  // same thread before that state update commits. Harmless in effect
  // (addReplies dedupes by id) but wastes a duplicate request every time —
  // a ref-based lock closes that gap the same way loadMoreComments/
  // FollowListModal/NotificationBell's loadMore already guard themselves.
  const loadingRepliesRef = useRef(new Set());
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editCommentText, setEditCommentText] = useState("");
  const [editSaving, setEditSaving] = useState(false);

  const [topLevelOffset, setTopLevelOffset] = useState(0);
  const [hasMoreComments, setHasMoreComments] = useState(true);
  const [loadingMoreComments, setLoadingMoreComments] = useState(false);
  const [supportsThreads, setSupportsThreads] = useState(true);
  const [loadingRepliesFor, setLoadingRepliesFor] = useState({}); // { [parentId]: true }
  // Whether the LAST fetch for a thread came back with a full page — not
  // derived from comparing the (blocked-filtered) local reply count against
  // the raw, unfiltered reply_count, which can never converge once a thread
  // has any reply from a blocked user and left "View more replies" stuck
  // forever. Same pre-filter-count pattern as FollowListModal/NotificationBell.
  const [hasMoreReplies, setHasMoreReplies] = useState({}); // { [parentId]: boolean }
  const [pendingDeleteIds, setPendingDeleteIds] = useState({}); // { [commentId]: timeoutId }
  const [deleteToast, setDeleteToast] = useState(null); // { id, message } | null
  // A block only removes ITS OWN direction's follow row and stops future
  // engagement, but doesn't delete comments already posted before the block
  // — every other surface (NotificationBell, FollowListModal, the feeds)
  // already filters these out; Comments.jsx was the one place a blocked
  // user's name/avatar/text stayed fully visible. Not state, since nothing
  // here needs to re-render when it resolves (it's only read from
  // visibleComments' filter below, same as pendingDeleteIds).
  const blockedIdsRef = useRef(new Set());
  // loadComments() and the getUser() effect below both fire independently
  // on mount with no ordering guarantee — getUser()'s path is strictly
  // heavier (two blocks queries plus a profile fetch), so loadComments
  // frequently wins the race and the first paint could briefly show a
  // blocked user's comment before blockedIdsRef finishes populating. Gating
  // the body on this (alongside `loading`) closes that gap, same as
  // NotificationBell.openPanel already does for its own panel.
  const [blockedReady, setBlockedReady] = useState(false);
  const pendingDeleteIdsRef = useRef(pendingDeleteIds);
  pendingDeleteIdsRef.current = pendingDeleteIds;
  // Closing this sheet (or the design it belongs to) mid-undo-window used to
  // leave a soft-deleted comment's setTimeout running in the background —
  // reopening within that window then refetched from the server before the
  // delayed delete had actually landed, showing the "deleted" comment again
  // as a ghost. Committing any still-pending deletes immediately on unmount
  // closes that gap: the undo grace period ends the moment this sheet does.
  const mountedRef = useRef(true);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      Object.entries(pendingDeleteIdsRef.current).forEach(([id, timer]) => {
        clearTimeout(timer);
        finalizeDeleteComment(id);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadComments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designId]);

  useEffect(() => {
    async function getUser() {
      const {
        data: { session }
      } = await supabase.auth.getSession();
      const user = session?.user ?? null;
      setUser(user);

      if (user) {
        const [{ data }, blockedIds] = await Promise.all([
          supabase.from("users").select("username, avatar_url").eq("id", user.id).maybeSingle(),
          getBlockedUserIds(user.id)
        ]);
        blockedIdsRef.current = new Set(blockedIds);
        // Triggers the re-render that lets visibleComments' filter below
        // pick up blockedIdsRef now that it's populated.
        setMyProfile(data || null);
      }
      setBlockedReady(true);
    }
    getUser();
  }, []);

  // Lock background scroll while the sheet is open, close on Escape.
  // Skipped while the report sub-modal is open — it has its own Escape
  // handler, and without this guard both fire, closing the report modal
  // AND the whole comments sheet underneath it in one keypress.
  useEffect(() => {
    const unlockScroll = lockBodyScroll();

    function onKeyDown(e) {
      if (e.key !== "Escape") return;
      if (reportCommentId) return;
      onClose?.();
    }
    window.addEventListener("keydown", onKeyDown);

    return () => {
      unlockScroll();
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose, reportCommentId]);

  // Keep the sheet inside the part of the screen that is really visible (above
  // the keyboard), and don't let a swipe on the sheet's header, the dimmed
  // area or the footer drag the whole page — and the sheet with it — around
  // behind the keyboard. Only the comment list itself and the text field
  // handle touch movement.
  const visibleBox = useVisibleViewport();
  const backdropRef = useRef(null);
  const sheetRef = useRef(null);
  useFocusTrap(sheetRef, true);
  useEffect(() => {
    const el = backdropRef.current;
    if (!el) return undefined;
    function blockPageDrag(e) {
      if (e.target.closest && e.target.closest(".comments-body, textarea, input")) return;
      e.preventDefault();
    }
    el.addEventListener("touchmove", blockPageDrag, { passive: false });
    return () => el.removeEventListener("touchmove", blockPageDrag);
  }, []);

  // Replies come 10 at a time, oldest first, and only for a thread someone opens
  // (see toggleReplies / loadMoreReplies) — never eagerly for every comment on the page.
  const REPLIES_PAGE = 10;

  async function fetchRepliesFor(parentId, after) {
    let query = supabase
      .from("comments")
      .select("*, users(username, avatar_url, verified)")
      .eq("parent_comment_id", parentId)
      .order("created_at", { ascending: true })
      .limit(REPLIES_PAGE);

    if (after) query = query.gt("created_at", after);

    const { data, error } = await query;

    if (error) {
      console.log("LOAD REPLIES ERROR:", error);
      return [];
    }

    return data || [];
  }

  async function loadComments() {
    setLoading(true);

    let { data, error } = await supabase
      .from("comments")
      .select("*, users(username, avatar_url, verified)")
      .eq("design_id", designId)
      .is("parent_comment_id", null)
      .order("created_at", { ascending: true })
      .range(0, PAGE_SIZE - 1);

    let threadsSupported = true;

    // The parent_comment_id column may not exist yet on older databases —
    // fall back to a flat comment list so it still works. Bounded even
    // here so a design with an unusually large comment count can't pull
    // every row in one shot.
    if (error?.code === "42703") {
      threadsSupported = false;
      ({ data, error } = await supabase
        .from("comments")
        .select("*, users(username, avatar_url, verified)")
        .eq("design_id", designId)
        .order("created_at", { ascending: true })
        .limit(200));
    }

    if (error) {
      console.log("LOAD COMMENTS ERROR:", error);
      setLoading(false);
      return;
    }

    setSupportsThreads(threadsSupported);

    const topLevel = data || [];

    if (!threadsSupported) {
      setComments(topLevel);
      setHasMoreComments(false);
      setLoading(false);
      return;
    }

    // Replies aren't fetched here — each thread's reply_count (a
    // trigger-maintained column) is enough to show the "View N replies"
    // button; the actual rows load lazily when someone opens a thread.
    setComments(topLevel);
    setTopLevelOffset(PAGE_SIZE);
    setHasMoreComments(topLevel.length === PAGE_SIZE);
    setLoading(false);
  }

  async function loadMoreComments() {
    if (!supportsThreads || loadingMoreComments || !hasMoreComments) return;
    setLoadingMoreComments(true);

    const { data, error } = await supabase
      .from("comments")
      .select("*, users(username, avatar_url, verified)")
      .eq("design_id", designId)
      .is("parent_comment_id", null)
      .order("created_at", { ascending: true })
      .range(topLevelOffset, topLevelOffset + PAGE_SIZE - 1);

    if (error) {
      console.log("LOAD MORE COMMENTS ERROR:", error);
      setLoadingMoreComments(false);
      return;
    }

    const newTopLevel = data || [];

    // A comment posted locally after the first page loaded (but before this
    // page was fetched) can reappear in this batch, since it now legitimately
    // falls within this server-side range — drop anything already in state.
    setComments(prev => {
      const existingIds = new Set(prev.map(c => c.id));
      const fresh = newTopLevel.filter(c => !existingIds.has(c.id));
      return [...prev, ...fresh];
    });
    setTopLevelOffset(prev => prev + PAGE_SIZE);
    setHasMoreComments(newTopLevel.length === PAGE_SIZE);
    setLoadingMoreComments(false);
  }

  async function addComment(e) {
    e.preventDefault();
    // disabled={posting} on the submit button only takes effect next
    // render — a fast double-tap/double-Enter can fire this twice before
    // that paints, inserting the same comment twice (and, for a reply,
    // double-bumping reply_count). Same class of guard LikeButton/
    // SaveButton/DownloadButton/ReportModal already use in this file group.
    if (postingRef.current) return;
    postingRef.current = true;
    setError(null);

    const clean = text.trim();
    if (!clean || !user) {
      postingRef.current = false;
      return;
    }

    setPosting(true);

    const parentId = replyingTo?.id || null;

    const payload = {
      design_id: designId,
      user_id: user.id,
      username: myProfile?.username || user.email.split("@")[0],
      comment: clean
    };
    // Only send parent_comment_id if we know the column exists — sending it
    // on a database that hasn't run the migration yet would error the insert.
    if (supportsThreads && parentId) {
      payload.parent_comment_id = parentId;
    }

    const { data: inserted, error } = await supabase
      .from("comments")
      .insert([payload])
      .select("*, users(username, avatar_url, verified)")
      .single();

    setPosting(false);
    postingRef.current = false;

    if (error) {
      console.log(error);
      setError(friendlyError(error, "Couldn't post that comment. Try again."));
      return;
    }

    setText("");
    const wasReplyTo = replyingTo;
    setReplyingTo(null);

    // Append locally instead of re-fetching — keeps whatever page of
    // comments/replies is already loaded intact.
    setComments(prev => [...prev, inserted]);

    // Replies notify whoever you replied to, not the design owner.
    const parentComment = wasReplyTo ? comments.find(c => c.id === wasReplyTo.id) : null;

    notifyMentions({
      text: clean,
      actorId: user.id,
      designId,
      commentText: clean,
      // Whoever's about to get the more specific "commented/replied"
      // notification below doesn't also need a "mentioned you" one for
      // this same comment (a reply's box is pre-filled with their
      // "@username", and a top-level comment can just as easily @mention
      // the design's own owner outright).
      excludeUserId: wasReplyTo ? parentComment?.user_id : designOwnerId
    });

    if (wasReplyTo) {
      if (parentComment) {
        createNotification({
          userId: parentComment.user_id,
          actorId: user.id,
          type: "comment",
          designId,
          commentText: clean
        });
      }
      // The DB trigger keeps reply_count in sync server-side, but this tab
      // won't see that until its own next fetch — bump it locally too so
      // the "View N replies" button is correct immediately.
      setComments(prev =>
        prev.map(c =>
          c.id === wasReplyTo.id ? { ...c, reply_count: (c.reply_count || 0) + 1 } : c
        )
      );
      setExpandedReplies(prev => ({ ...prev, [wasReplyTo.id]: true }));
    } else {
      createNotification({
        userId: designOwnerId,
        actorId: user.id,
        type: "comment",
        designId,
        commentText: clean
      });
    }
  }

  function startReply(comment) {
    if (!user) return;
    setReplyingTo({ id: comment.id, username: comment.users?.username || comment.username });
    setText(`@${comment.users?.username || comment.username} `);
  }

  function cancelReply() {
    setReplyingTo(null);
    setText("");
  }

  // Replies load lazily, the first time a thread is actually opened —
  // fetchRepliesFor is bounded (MAX_REPLIES_PER_THREAD), so opening even a
  // heavily-replied-to comment can't pull an unlimited number of rows.
  // Adds fetched replies to the list, leaving out any already there (a reply you just
  // posted yourself is already in it).
  function addReplies(replies) {
    setComments(prev => {
      const have = new Set(prev.map(c => c.id));
      return [...prev, ...replies.filter(r => !have.has(r.id))];
    });
  }

  async function loadRepliesPage(comment) {
    const commentId = comment.id;
    if (loadingRepliesRef.current.has(commentId)) return;
    loadingRepliesRef.current.add(commentId);
    setLoadingRepliesFor(prev => ({ ...prev, [commentId]: true }));
    const replies = await fetchRepliesFor(commentId, replyCursor.current[commentId] || null);
    if (replies.length > 0) {
      replyCursor.current[commentId] = replies[replies.length - 1].created_at;
      addReplies(replies);
    }
    setHasMoreReplies(prev => ({ ...prev, [commentId]: replies.length === REPLIES_PAGE }));
    setLoadingRepliesFor(prev => ({ ...prev, [commentId]: false }));
    loadingRepliesRef.current.delete(commentId);
  }

  async function toggleReplies(comment) {
    const commentId = comment.id;
    const isExpanded = !!expandedReplies[commentId];

    if (isExpanded) {
      setExpandedReplies(prev => ({ ...prev, [commentId]: false }));
      return;
    }

    // Nothing fetched from the server for this thread yet (a reply you posted
    // yourself doesn't count — the others are still to come).
    if (replyCursor.current[commentId] === undefined && comment.reply_count > 0) {
      await loadRepliesPage(comment);
    }

    setExpandedReplies(prev => ({ ...prev, [commentId]: true }));
  }

  // Soft-delete: the comment is hidden right away (filtered out of the
  // list below via pendingDeleteIds) and only actually removed from the
  // database once the undo toast expires without being clicked.
  function deleteComment(id) {
    // A fast double-tap on the same comment's delete button, before the
    // first click's re-render removes it from view, used to leak the FIRST
    // timer: the forEach below only finalizes OTHER pending ids, so the
    // second call's setPendingDeleteIds just overwrote pendingDeleteIds[id]
    // with a new timer — orphaning the old one with nothing left able to
    // clearTimeout it. Undo then only ever reached the second timer, so the
    // comment still got permanently deleted a few seconds later regardless
    // of clicking Undo.
    if (pendingDeleteIdsRef.current[id]) return;

    // Only one soft-delete can be "pending undo" at a time — there's a
    // single toast slot. Starting a new one used to leave any earlier
    // pending delete's timer running invisibly in the background with no
    // way left to undo it; finalizing that one immediately instead keeps
    // the behavior honest, matching what the toast can actually show.
    Object.entries(pendingDeleteIdsRef.current).forEach(([pendingId, timer]) => {
      if (pendingId !== id) {
        clearTimeout(timer);
        finalizeDeleteComment(pendingId);
      }
    });

    const timer = setTimeout(() => finalizeDeleteComment(id), 5000);
    setPendingDeleteIds(prev => ({ ...prev, [id]: timer }));
    setDeleteToast({ id, message: "Comment deleted" });
  }

  async function finalizeDeleteComment(id) {
    if (mountedRef.current) {
      setPendingDeleteIds(prev => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }

    // The delete always runs, even after this sheet has been closed —
    // only the UI updates below are skipped once that happens.
    const { error } = await supabase.from("comments").delete().eq("id", id);

    if (error) {
      console.log("DELETE COMMENT ERROR:", error);
      return;
    }

    if (!mountedRef.current) return;

    setComments(prev => {
      const target = prev.find(c => c.id === id);
      const next = prev.filter(c => c.id !== id);

      // Deleting a reply should bring its parent's reply_count down too —
      // same reasoning as the optimistic bump when a reply is posted.
      if (target?.parent_comment_id) {
        return next.map(c =>
          c.id === target.parent_comment_id
            ? { ...c, reply_count: Math.max(0, (c.reply_count || 0) - 1) }
            : c
        );
      }

      return next;
    });
  }

  function undoDeleteComment(id) {
    const timer = pendingDeleteIds[id];
    if (timer) clearTimeout(timer);

    setPendingDeleteIds(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setDeleteToast(null);
  }

  function startEditComment(c) {
    setEditingCommentId(c.id);
    setEditCommentText(c.comment);
  }

  function cancelEditComment() {
    setEditingCommentId(null);
    setEditCommentText("");
  }

  async function saveEditComment(id) {
    const clean = editCommentText.trim();
    if (!clean) return;

    setEditSaving(true);

    const { error } = await supabase
      .from("comments")
      .update({ comment: clean })
      .eq("id", id);

    setEditSaving(false);

    if (error) {
      console.log("EDIT COMMENT ERROR:", error);
      return;
    }

    setComments(prev => prev.map(c => (c.id === id ? { ...c, comment: clean } : c)));
    setEditingCommentId(null);
    setEditCommentText("");
  }

  function handleBackdropClick(e) {
    if (e.target === e.currentTarget) onClose?.();
  }

  function renderCommentRow(c, isReply) {
    const displayName = c.users?.username || c.username;
    const avatarUrl = c.users?.avatar_url;
    const isOwner = user && user.id === c.user_id;
    const isEditing = editingCommentId === c.id;

    return (
      <div key={c.id} style={isReply ? replyRow : commentRow}>
        <LazyImage
          src={avatarUrl || "/default-avatar.svg"}
          alt={displayName}
          style={isReply ? replyAvatar : commentAvatar}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          {isEditing ? (
            <>
              <HashtagInput
                value={editCommentText}
                onChange={setEditCommentText}
                maxLength={500}
                inputStyle={editInput}
                autoFocus
              />
              <div style={editActionsRow}>
                <span style={editSaveBtn} onClick={() => !editSaving && saveEditComment(c.id)}>
                  {editSaving ? "Saving…" : "Save"}
                </span>
                <span style={editCancelBtn} onClick={cancelEditComment}>
                  Cancel
                </span>
              </div>
            </>
          ) : (
            <>
              <div style={nameLine}>
                <strong style={nameText}>
                  @{displayName}
                  {c.users?.verified && <VerifiedBadge size={15} />}
                </strong>
                <span style={commentMeta}>{timeAgo(c.created_at)}</span>
              </div>
              <p style={commentText}>
                <MentionText text={c.comment} />
              </p>
              {((!isReply && user && supportsThreads) || isOwner) && (
                <div style={commentActionsRow}>
                  {!isReply && user && supportsThreads && (
                    <span style={replyLink} onClick={() => startReply(c)}>
                      {t("comments.reply")}
                    </span>
                  )}
                  {isOwner && (
                    <span style={replyLink} onClick={() => startEditComment(c)}>
                      Edit
                    </span>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {isEditing ? null : isOwner ? (
          <button
            onClick={() => deleteComment(c.id)}
            style={deleteBtn}
            aria-label="Delete comment"
          >
            <IoTrashOutline />
          </button>
        ) : (
          user && (
            <button
              onClick={() => setReportCommentId(c.id)}
              style={deleteBtn}
              aria-label="Report comment"
            >
              <IoFlagOutline />
            </button>
          )
        )}
      </div>
    );
  }

  const visibleComments = comments.filter(c => !pendingDeleteIds[c.id] && !blockedIdsRef.current.has(c.user_id));
  const topLevelComments = visibleComments.filter(c => !c.parent_comment_id);
  const repliesByParent = {};
  visibleComments.forEach(c => {
    if (c.parent_comment_id) {
      if (!repliesByParent[c.parent_comment_id]) repliesByParent[c.parent_comment_id] = [];
      repliesByParent[c.parent_comment_id].push(c);
    }
  });

  return (
    <>
    {createPortal(
    <div
      className="comments-backdrop"
      ref={backdropRef}
      onClick={handleBackdropClick}
      style={visibleBox ? { top: visibleBox.top, bottom: "auto", height: visibleBox.height } : undefined}
    >
      <div className="comments-sheet" ref={sheetRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label="Comments">
        <style>{`
          .comments-backdrop {
            position: fixed;
            inset: 0;
            background: rgba(20, 14, 8, 0.55);
            z-index: 1000;
            display: flex;
            align-items: flex-end;
            justify-content: center;
          }

          .comments-sheet {
            background: var(--bg-surface);
            width: 100%;
            max-height: 82%;
            border-radius: 18px 18px 0 0;
            display: flex;
            flex-direction: column;
            box-shadow: 0 -4px 24px rgba(0,0,0,0.2);
            animation: comments-slide-up 0.22s ease-out;
          }

          @keyframes comments-slide-up {
            from { transform: translateY(100%); }
            to { transform: translateY(0); }
          }

          .comments-handle {
            width: 40px;
            height: 4px;
            border-radius: 2px;
            background: var(--border-input);
            margin: 10px auto 0;
            flex-shrink: 0;
          }

          .comments-header {
            display: flex;
            align-items: center;
            justify-content: center;
            position: relative;
            padding: 12px 16px 12px;
            border-bottom: 1px solid var(--border-soft);
            flex-shrink: 0;
          }

          .comments-close {
            position: absolute;
            right: 12px;
            top: 50%;
            transform: translateY(-50%);
            display: flex;
            align-items: center;
            justify-content: center;
            width: 32px;
            height: 32px;
            background: var(--bg-soft);
            border: none;
            border-radius: 50%;
            font-size: 18px;
            line-height: 1;
            cursor: pointer;
            color: var(--text-body);
            padding: 0;
          }

          .comments-body {
            flex: 1;
            overflow-y: auto;
            padding: 6px 16px 10px;
            -webkit-overflow-scrolling: touch;
            overscroll-behavior: contain;
          }

          .comments-footer {
            flex-shrink: 0;
            padding: 10px 16px calc(10px + env(safe-area-inset-bottom));
            border-top: 1px solid var(--border-soft);
            background: var(--bg-surface);
          }

          .comments-footer input:focus {
            border-color: var(--accent-primary) !important;
            background: var(--bg-surface) !important;
          }

          /* ===== Tablet / desktop: centered dialog instead of bottom sheet ===== */
          @media (min-width: 641px) {
            .comments-backdrop {
              align-items: center;
            }
            .comments-sheet {
              width: 92%;
              max-width: 480px;
              max-height: 80vh;
              border-radius: 16px;
              animation: comments-pop-in 0.18s ease-out;
            }
            .comments-handle {
              display: none;
            }
            @keyframes comments-pop-in {
              from { transform: scale(0.96); opacity: 0; }
              to { transform: scale(1); opacity: 1; }
            }
          }
        `}</style>

        <div className="comments-handle" />
        <div className="comments-header">
          <strong style={{ fontSize: 16, fontWeight: 800, color: "var(--text-heading)" }}>{t("comments.title")}</strong>
          <button className="comments-close" onClick={onClose} aria-label="Close comments">
            <IoClose />
          </button>
        </div>

        <div className="comments-body">
          {loading || !blockedReady ? (
            <p style={{ ...mutedText, textAlign: "center", padding: "28px 0" }}>Loading comments…</p>
          ) : visibleComments.length === 0 ? (
            <div style={emptyState}>
              <span style={emptyIcon} aria-hidden="true">
                <IoChatbubbleOutline size={24} />
              </span>
              <p style={emptyText}>{t("comments.empty")}</p>
            </div>
          ) : (
            topLevelComments.map(c => {
              const replies = repliesByParent[c.id] || [];
              const isExpanded = !!expandedReplies[c.id];
              const replyCount = c.reply_count || 0;

              return (
                <div key={c.id} style={threadWrapper}>
                  {renderCommentRow(c, false)}

                  {replyCount > 0 && (
                    <button
                      style={viewRepliesBtn}
                      onClick={() => toggleReplies(c)}
                      disabled={!!loadingRepliesFor[c.id]}
                    >
                      <span style={replyLine} />
                      {loadingRepliesFor[c.id]
                        ? "Loading…"
                        : isExpanded
                        ? "Hide replies"
                        : `View ${replyCount} ${replyCount === 1 ? "reply" : "replies"}`}
                    </button>
                  )}

                  {isExpanded && replies.map(r => renderCommentRow(r, true))}

                  {isExpanded && hasMoreReplies[c.id] && (
                    <button
                      style={viewRepliesBtn}
                      onClick={() => loadRepliesPage(c)}
                      disabled={!!loadingRepliesFor[c.id]}
                    >
                      <span style={replyLine} />
                      {loadingRepliesFor[c.id] ? "Loading…" : "View more replies"}
                    </button>
                  )}
                </div>
              );
            })
          )}
          {hasMoreComments && (
            <button style={viewMoreCommentsBtn} onClick={loadMoreComments} disabled={loadingMoreComments}>
              {loadingMoreComments ? "Loading…" : "View more comments"}
            </button>
          )}
          {error && <p style={errorText}>{error}</p>}
        </div>

        <div className="comments-footer">
          {replyingTo && (
            <div style={replyingBanner}>
              <span>
                Replying to <strong>@{replyingTo.username}</strong>
              </span>
              <span style={cancelReplyBtn} onClick={cancelReply} role="button" aria-label="Cancel reply">
                <IoClose size={16} />
              </span>
            </div>
          )}
          {user ? (
            <form onSubmit={addComment} style={formRow}>
              <img
                src={myProfile?.avatar_url || "/default-avatar.svg"}
                alt="Your avatar"
                style={commentAvatar}
              />
              <div style={{ flex: 1 }}>
                <HashtagInput
                  value={text}
                  onChange={setText}
                  placeholder={t("comments.placeholder")}
                  maxLength={500}
                  inputStyle={input}
                  autoFocus
                />
              </div>
              <button type="submit" style={sendBtn} disabled={posting || !text.trim() || !myProfile} aria-label="Post comment">
                {posting ? "…" : <IoSend size={16} />}
              </button>
            </form>
          ) : (
            <p style={mutedText}>{t("comments.loginToComment")}</p>
          )}
        </div>
      </div>
    </div>,
    document.body
    )}

    {reportCommentId && (
      <ReportModal
        reporterId={user?.id}
        targetType="comment"
        targetId={reportCommentId}
        onClose={() => setReportCommentId(null)}
      />
    )}

    {deleteToast && (
      <UndoToast
        // Keyed on the comment id, not left to default reconciliation —
        // deleting a second comment while the first's undo toast is still up
        // shows the same static "Comment deleted" text both times, so
        // without a key React just updates props on the existing instance
        // instead of mounting a new one. That left the new comment's undo
        // window inheriting whatever time was left on the first timer,
        // sometimes making Undo disappear seconds early.
        key={deleteToast.id}
        text={deleteToast.message}
        onUndo={() => undoDeleteComment(deleteToast.id)}
        onExpire={() => setDeleteToast(null)}
      />
    )}
    </>
  );
}

/* ===== Keeps the sheet above the on-screen keyboard =====
   With the keyboard open, phone browsers keep the page at its full size and
   only shrink the part you can see (the "visual viewport") — and drag that
   part around when you swipe. A sheet pinned with position:fixed belongs to
   the full-size page, so it slid out of view (or behind the keyboard) as soon
   as the person scrolled. Returns { top, height } of the visible part while it
   differs from the full page, and null otherwise, so the sheet behaves exactly
   as before when no keyboard is up. */
function useVisibleViewport() {
  const [box, setBox] = useState(null);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return undefined;

    function update() {
      // Pinch-zoomed in: the numbers mean something else, leave the sheet be.
      if (vv.scale > 1.01) {
        setBox(null);
        return;
      }
      const differs = Math.abs(vv.height - window.innerHeight) > 1 || vv.offsetTop > 1;
      setBox(prev => {
        if (!differs) return prev === null ? prev : null;
        if (prev && prev.top === vv.offsetTop && prev.height === vv.height) return prev;
        return { top: vv.offsetTop, height: vv.height };
      });
    }

    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
    };
  }, []);

  return box;
}

/* ===== Relative time helper ===== */
function timeAgo(dateString) {
  const seconds = Math.floor((Date.now() - new Date(dateString).getTime()) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateString).toLocaleDateString();
}

/* ===== STYLES (Virsa palette, matches Home/Profile/Login) ===== */

// Each thread is a comment plus its replies; spacing (not divider lines) keeps
// the list light. Replies sit under the comment's text, so they are indented by
// the avatar's width plus its gap.
const threadWrapper = {
  padding: "10px 0"
};

const commentRow = {
  display: "flex",
  gap: 10,
  alignItems: "flex-start"
};

const replyRow = {
  ...commentRow,
  marginTop: 10,
  marginLeft: 46
};

const commentAvatar = {
  width: 36,
  height: 36,
  borderRadius: "50%",
  objectFit: "cover",
  border: "1px solid var(--border-soft)",
  flexShrink: 0
};

const replyAvatar = {
  ...commentAvatar,
  width: 28,
  height: 28
};

const nameLine = {
  display: "flex",
  alignItems: "baseline",
  flexWrap: "wrap",
  columnGap: 8
};

const nameText = { color: "var(--text-primary)", fontSize: 13.5, fontWeight: 700 };

const commentText = {
  margin: "2px 0 0",
  fontSize: 14,
  color: "var(--text-heading)",
  lineHeight: 1.45,
  wordBreak: "break-word"
};

const commentActionsRow = {
  display: "flex",
  alignItems: "center",
  gap: 18,
  marginTop: 2
};

const commentMeta = { fontSize: 11.5, color: "var(--text-muted)" };

const replyLink = {
  fontSize: 12,
  fontWeight: 700,
  color: "var(--text-muted)",
  cursor: "pointer",
  padding: "5px 0"
};

const editInput = {
  padding: "6px 10px",
  fontSize: 13.5,
  borderRadius: 14,
};

const editActionsRow = {
  display: "flex",
  gap: 14,
  marginTop: 6
};

const editSaveBtn = {
  fontSize: 12,
  fontWeight: 700,
  color: "var(--accent-primary)",
  cursor: "pointer"
};

const editCancelBtn = {
  fontSize: 12,
  fontWeight: 700,
  color: "var(--text-muted)",
  cursor: "pointer"
};

const viewRepliesBtn = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  marginLeft: 46,
  marginTop: 8,
  padding: 0,
  border: "none",
  background: "none",
  fontSize: 12.5,
  fontWeight: 600,
  color: "var(--text-muted)",
  cursor: "pointer"
};

const viewMoreCommentsBtn = {
  display: "block",
  width: "100%",
  textAlign: "center",
  padding: "10px 0",
  border: "none",
  background: "none",
  fontSize: 13,
  fontWeight: 700,
  color: "var(--accent-primary)",
  cursor: "pointer"
};

const replyLine = {
  display: "inline-block",
  width: 20,
  height: 1,
  background: "var(--border-input)"
};

const replyingBanner = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
  fontSize: 12.5,
  color: "var(--text-body)",
  background: "var(--bg-soft)",
  borderRadius: 10,
  padding: "6px 8px 6px 12px",
  marginBottom: 8
};

const cancelReplyBtn = {
  display: "flex",
  alignItems: "center",
  cursor: "pointer",
  color: "var(--text-muted)",
  padding: 2
};

// Quiet by default (delete / report are rarely wanted); the padding is what makes it easy to tap.
const deleteBtn = {
  display: "flex",
  background: "none",
  border: "none",
  cursor: "pointer",
  fontSize: 16,
  color: "var(--text-muted)",
  opacity: 0.7,
  flexShrink: 0,
  padding: 6,
  margin: "-4px -6px 0 0"
};

const formRow = {
  display: "flex",
  alignItems: "center",
  gap: 10
};

const input = {
  flex: 1,
  boxSizing: "border-box",
  padding: "10px 14px",
  borderRadius: 22,
  border: "1.5px solid var(--border-soft)",
  background: "var(--bg-soft)",
  color: "var(--text-heading)",
  fontSize: 14,
  outline: "none"
};

const sendBtn = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 40,
  height: 40,
  borderRadius: "50%",
  border: "none",
  background: "var(--accent-primary)",
  color: "var(--accent-contrast)",
  fontSize: 13,
  cursor: "pointer",
  flexShrink: 0,
  transition: "opacity 0.15s"
};

const emptyState = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 10,
  padding: "36px 20px"
};

const emptyIcon = {
  width: 52,
  height: 52,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "var(--bg-soft)",
  color: "var(--accent-primary)"
};

const emptyText = { margin: 0, fontSize: 13.5, color: "var(--text-muted)", textAlign: "center" };

const mutedText = { fontSize: 13, color: "var(--text-muted)", margin: "8px 0" };
const errorText = { fontSize: 12.5, color: "var(--accent-danger)", marginTop: 6 };
